/**
 * The "Test-E-Mail senden" button of the template tab and its dialog
 * (TEST-EMAIL T3).
 *
 * RENDERED ONLY WHERE THE SERVER OFFERS A TEST SEND. The caller mounts this for
 * an automation whose `capabilities.test_email` is `true` — no key list, no
 * category, no runtime mode decides it — and for no other; a type without test
 * sends shows no control at all, not a disabled one.
 *
 * NOT FROM AN UNSAVED TEMPLATE. The server sends the template it has STORED,
 * never the draft on screen. While the template tab holds unsaved work — a new
 * own template, an edit, an open review, a save on its way — the button stays
 * where it is but does not open the dialog (`blocked`); it is `aria-disabled`,
 * not `disabled`, so it keeps its focus and its description — the caller's
 * sentence asking to save first.
 *
 * A SWITCHED-OFF AUTOMATION CAN BE TESTED. `enabled` plays no part: a test send
 * triggers nothing of the automation itself.
 *
 * AFTER AN ATTEMPT, THE PROTOKOLL. A send the server may have recorded — sent,
 * rejected, unclear, or failed unexpectedly — is reported to `onAttempted` with
 * the automation it was made for, so the page can re-read that automation's
 * Protokoll if it is the one on screen (Regel 49). A refusal records nothing
 * and reports nothing.
 *
 * AN ANSWER IS NEVER LOST (adversarial review). The send outlives this
 * component (`automationTestEmailSends`): an answer arriving while no dialog
 * for this automation is open, or shown in a dialog that then disappears
 * without the reader closing it, is said as a toast — in the dialog's words,
 * named after the automation, the unclear outcome as a warning that stays.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import AutomationTestEmailDialog from "./AutomationTestEmailDialog";
import {
  TEST_EMAIL_ACTION_LABEL,
  describeSendError,
  outcomeNote,
  sentMessage,
} from "./automationTestEmailModel";
import useAutomationTestEmail from "./useAutomationTestEmail";

const SECONDARY_BUTTON =
  "inline-flex items-center justify-center rounded-[9px] border border-border bg-card px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-card max-md:min-h-9";

/** How long an unclear answer stays when it is said outside the dialog. */
const UNCLEAR_TOAST_MS = 20000;

/** The answer as a toast, for a dialog that cannot say it any more. */
function reporterFor(label) {
  return (entry) => {
    if (entry.status === "sent") {
      toast.success(`${label}: ${sentMessage(entry.result?.recipient)}`);
      return;
    }
    const failure = describeSendError(entry.error);
    const text = `${label}: ${[failure.message, outcomeNote(failure)].filter(Boolean).join(" ")}`;
    if (failure.kind === "indeterminate" || failure.kind === "error") {
      toast.warning(text, { duration: UNCLEAR_TOAST_MS });
    } else {
      toast.error(text);
    }
  };
}

export default function AutomationTestEmailAction({
  automation,
  blocked = false,
  describedBy,
  onAttempted,
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null);
  const closedByReader = useRef(false);
  const testEmail = useAutomationTestEmail(automation.key);
  const { dialogOpened, dialogClosed } = testEmail;

  // While the dialog is open, an answer for this automation is shown in it;
  // when it goes, the store learns whether the reader closed it or it vanished.
  useEffect(() => {
    if (!open) return undefined;
    closedByReader.current = false;
    dialogOpened();
    return () => {
      dialogClosed(closedByReader.current);
    };
  }, [open, dialogOpened, dialogClosed]);

  const openDialog = () => {
    if (blocked || open) return;
    setOpen(true);
    testEmail.begin();
  };

  const changeOpen = (next) => {
    if (next) return;
    closedByReader.current = true;
    setOpen(false);
    testEmail.end();
  };

  // Back to the button that opened it, if it is still there.
  const returnFocus = (event) => {
    event.preventDefault();
    const el = trigger.current;
    if (el && el.isConnected) el.focus();
  };

  const send = async (scenarioKey) => {
    const key = automation.key;
    const outcome = await testEmail.send(scenarioKey, reporterFor(automation.label));
    if (!outcome) return;
    const attempted = outcome.ok || describeSendError(outcome.error).attempted;
    if (attempted && onAttempted) onAttempted(key);
  };

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={openDialog}
        aria-haspopup="dialog"
        aria-disabled={blocked ? "true" : undefined}
        aria-describedby={blocked && describedBy ? describedBy : undefined}
        className={SECONDARY_BUTTON}
      >
        {TEST_EMAIL_ACTION_LABEL}
      </button>
      <AutomationTestEmailDialog
        open={open}
        onOpenChange={changeOpen}
        onCloseAutoFocus={returnFocus}
        automationLabel={automation.label}
        readiness={testEmail.readiness}
        attempt={testEmail.attempt}
        busy={testEmail.busy}
        mine={testEmail.mine}
        locked={testEmail.locked}
        onRetryReadiness={testEmail.retryReadiness}
        onSend={send}
        onScenarioChange={testEmail.clearAttempt}
        onLeave={testEmail.acknowledge}
      />
    </>
  );
}
